// src/components/Dashboard/EmployeeInfoTable.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftCircle, XCircle } from 'lucide-react'; // Import icons
import { Typography } from '@mui/material'; // Assuming Material-UI Typography is available

// Utility function to combine Tailwind CSS classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism styles (re-used for consistency)
const glassmorphismBase = 'bg-white/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] overflow-hidden';
const glassmorphismHeader = 'bg-neutral-50/40 rounded-xl shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px]';

/**
 * EmployeeInfoTable displays a detailed table of all employees.
 * It takes an employees array and a close callback as props.
 *
 * @param {Object[]} employees - An array of employee objects to display.
 * @param {function(): void} onClose - Callback function to close this view.
 */
const EmployeeInfoTable = ({ employees = [], onClose }) => {
    return (
        <motion.div
            className={combineClasses("self-stretch relative w-full h-full p-4 md:p-6 lg:p-8", glassmorphismBase)}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
        >
            {/* Header */}
            <div className={combineClasses("w-full h-12 flex items-center px-4 md:px-6", glassmorphismHeader)}>
                {/* <div
                    className="w-8 h-8 flex items-center justify-center cursor-pointer"
                    onClick={onClose} // Back button click
                    title="Go Back"
                > */}
                    {/* <div className="w-8 h-8 bg-neutral-50/50 rounded-full shadow-lg flex items-center justify-center">
                        <ArrowLeftCircle className="w-6 h-6 text-black" />
                    </div> */}
                {/* </div> */}
                <Typography variant="h5" className="ml-4 md:ml-8 text-black font-bold font-['Figtree'] leading-normal">
                    All Employees
                </Typography>
                <div
                    className="w-8 h-8 ml-auto flex items-center justify-center cursor-pointer"
                    onClick={onClose} // Close button click
                    title="Close"
                >
                    <div className="w-8 h-8 bg-neutral-50/50 rounded-full shadow-lg flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-black" />
                    </div>
                </div>
            </div>

            {/* Table Content */}
            <div className="w-full mt-6 overflow-x-auto custom-scrollbar" style={{ maxHeight: 'calc(100% - 100px)' }}> {/* Adjust max height */}
                <table className="min-w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="px-3 py-4 bg-neutral-50/10 rounded-tl-lg text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Image</th>
                            <th className="px-12 py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Name</th>
                            <th className="px-14 py-4 bg-neutral-50/10 text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Role</th>
                            {/* <th className="px-3 py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Office ID</th> */}
                            <th className="px-9 py-4 bg-neutral-50/10 text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Status</th>
                            <th className="py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-tr-lg text-center text-black text-xl font-semibold font-['Open_Sans'] leading-normal">Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {employees.length > 0 ? (
                            employees.map((employee, index) => (
                                <tr key={employee.id || index}>
                                    <td className="px-3 py-4 bg-neutral-50/10 text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">
                                        <img src={employee.image} alt={employee.name} className="w-10 h-10 rounded-full mx-auto" />
                                    </td>
                                    <td className="px-12 py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{employee.name}</td>
                                    <td className="px-14 py-4 bg-neutral-50/10 text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{employee.role}</td>
                                    {/* <td className="px-3 py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{employee.officeId}</td> */}
                                    <td className="px-9 py-4 bg-neutral-50/10 text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">
                                        <span className={combineClasses(
                                            "px-3 py-1 rounded-full text-xs font-semibold",
                                            employee.status === 'On Duty' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        )}>
                                            {employee.status}
                                        </span>
                                    </td>
                                    <td className="py-4 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">{employee.note || 'N/A'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="py-4 text-center text-gray-600 bg-white/80">No employee data available.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </motion.div>
    );
};

export default EmployeeInfoTable;
